import { Injectable } from "@angular/core";
import { files } from "dropbox";
import { SettingsService } from "../settings/settings.service";
import { DropboxAuthService } from "./dropbox/dropbox-auth.service";
import { CloudServiceEnum } from "./enums";
import { GapiSession } from "./google-drive/gapi.session";
import { DriveMetadata } from "./metadata/DriveMetadata";
import { OneDriveItemMetadata } from "./one-drive/one-drive.api.service";
import { OneDriveAuthService } from "./one-drive/one-drive.auth.service";

@Injectable({
  providedIn: 'root'
})
export class CloudRecentFilesService {
  private RECENT_FILES_STORAGE_CAPACITY: number = 10; // amount of recent files

  constructor(
    private readonly settingsService: SettingsService,
    private readonly gapiService: GapiSession,
    private readonly oneDriveService: OneDriveAuthService,
    private readonly dropBoxService: DropboxAuthService,
    ) {
  }

  async isRecentFilesStorageNotEmpty(cloud: CloudServiceEnum): Promise<boolean> {
    if (this.settingsService.settings) {
      const { cloudRecentFiles: recentFiles } = this.settingsService.settings;

      if (recentFiles && recentFiles.length) {
        const accountId = await this.getCloudAccount(cloud);
        let cloudRecentFiles = recentFiles.find(cl => cl.cloud === cloud && cl.accountId === accountId);

        if (cloudRecentFiles) {
          return !!cloudRecentFiles.files.length;
        }
      }
    }

    return false;
  }

  async getFiles(cloud: CloudServiceEnum): Promise<Array<DriveMetadata | OneDriveItemMetadata | files.FileMetadataReference>> {
    if (this.settingsService.settings) {
      const { cloudRecentFiles: recentFiles } = this.settingsService.settings;

      if (recentFiles && recentFiles.length) {
        const accountId = await this.getCloudAccount(cloud);
        let cloudRecentFiles = recentFiles.find(cl => cl.cloud === cloud && cl.accountId === accountId);

        if (cloudRecentFiles) {
          const { files } = cloudRecentFiles

          return files;
        } else {
          this.clearCloudRecentFiles(cloud);
        }
      }
    }

    this.clearCloudRecentFiles(cloud);

    return [];
  }

  async addFileToRecent(file: DriveMetadata | OneDriveItemMetadata | files.FileMetadataReference, cloud: CloudServiceEnum): Promise<void> {
    if (this.settingsService.settings) {

      let { cloudRecentFiles: recentFiles } = this.settingsService.settings;

      if (recentFiles === null) {
        recentFiles = [];
      }

      const accountId = await this.getCloudAccount(cloud);

      if (accountId === null) {
        return;
      }

      const cloudRecentFiles = recentFiles.find(f => f.cloud === cloud && f.accountId === accountId);

      if (cloudRecentFiles) {
        const { files } = cloudRecentFiles;

        const existedFile = files.find(fl => fl.id === file.id);

        if (existedFile) {
          cloudRecentFiles.files = [existedFile, ...files.filter(fl => fl.id !== existedFile.id)];
        } else {
          cloudRecentFiles.files = [file, ...files].slice(0, this.RECENT_FILES_STORAGE_CAPACITY);
        }

        const updatedRecentFiles = [...recentFiles.filter(cl => cl.cloud !== cloud && cl.accountId !== accountId), cloudRecentFiles]
        this.settingsService.saveSettings({ cloudRecentFiles: updatedRecentFiles });
        return;
      } else if (recentFiles.find(f => f.cloud === cloud)) {
        this.clearCloudRecentFiles(cloud);
      }

      recentFiles = [...recentFiles, {accountId: accountId || '', cloud: cloud, files: [file]}];
      this.settingsService.saveSettings({ cloudRecentFiles: recentFiles });
    }
  }

  async removeFileFromRecent(fileId: string, cloud: CloudServiceEnum): Promise<void> {
    if (this.settingsService.settings && this.settingsService.settings.cloudRecentFiles) {
      const { cloudRecentFiles: recentFiles } = this.settingsService.settings;

      if (recentFiles) {
        const accountId = await this.getCloudAccount(cloud);
        const cloudRecentFiles = recentFiles.find(cl => cl.cloud === cloud && cl.accountId === accountId);

        if (cloudRecentFiles) {
          const { files } = cloudRecentFiles
          cloudRecentFiles.files = files.filter(f => f.id !== fileId);

          const updatedRecentFiles = [...recentFiles.filter(cl => cl.cloud !== cloud), cloudRecentFiles]

          this.settingsService.saveSettings({ cloudRecentFiles: updatedRecentFiles });
        } else {
          this.clearCloudRecentFiles(cloud);
        }
      }
    }
  }

  clearCloudRecentFiles(cloud: CloudServiceEnum) {
    if (this.settingsService.settings && this.settingsService.settings.cloudRecentFiles) {
      let { cloudRecentFiles } = this.settingsService.settings;

      this.settingsService.saveSettings({ cloudRecentFiles: cloudRecentFiles.filter(f => f.cloud !== cloud) });
    }
  }

  private async getCloudAccount(cloud: CloudServiceEnum): Promise<string | null> {
    switch (cloud) {
      case CloudServiceEnum.DropBox:
        const dbxProfile = await this.dropBoxService.getUserInfo();
        return dbxProfile?.account_id || null;
      case CloudServiceEnum.GoogleDrive:
        const gdriveProfile = await this.gapiService.getUserInfo();
        return gdriveProfile;
      case CloudServiceEnum.OneDrive:
        const oneDriveProfile = await this.oneDriveService.getUserInfo();
        return oneDriveProfile?.id || null;
      default: return null;
    }
  }
}
